import classNames from 'classnames';
import {ChangeEvent, ComponentProps, FC, useCallback, useState} from 'react';
import InputMask from 'react-input-mask';

import {Icon} from 'components/Icon/Icon';
import {Tooltip, TooltipType} from 'components/Tooltip/Tooltip';

import styles from './TextField.module.css';

export enum TextFieldType {
    WhiteBgStroke = 'WhiteBgStroke',
    WhiteBgFill = 'WhiteBgFill',
    DarkBgFill = 'DarkBgFill',
}

const TYPE_CLASS_NAMES = {
    [TextFieldType.WhiteBgStroke]: styles.whiteBgStroke,
    [TextFieldType.WhiteBgFill]: styles.whiteBgFill,
    [TextFieldType.DarkBgFill]: styles.darkBgFill,
};

type Props = {
    value?: string;
    label: string;
    name: string;
    error?: string;
    className?: string;
    inputClassName?: string;
    required?: boolean;
    readonly?: boolean;
    disabled?: boolean;
    hideClearButton?: boolean;
    hideErrorTooltip?: boolean;
    type?: TextFieldType;
    nativeType?: 'text' | 'number' | 'textarea';
    onChange: (value: string) => void;
    onFocus?: () => void;
    onBlur?: () => void;

    mask?: ComponentProps<typeof InputMask>['mask'];
    maskPlaceholder?: ComponentProps<typeof InputMask>['maskPlaceholder'];
    alwaysShowMask?: ComponentProps<typeof InputMask>['alwaysShowMask'];
    beforeMaskedStateChange?: ComponentProps<
        typeof InputMask
    >['beforeMaskedStateChange'];
};

export const TextField: FC<Props> = ({
    value = '',
    label,
    type = TextFieldType.WhiteBgFill,
    error,
    required,
    className,
    inputClassName,
    name,
    nativeType = 'text',
    mask = '',
    maskPlaceholder,
    beforeMaskedStateChange,
    alwaysShowMask,
    readonly,
    hideClearButton,
    disabled,
    hideErrorTooltip,
    onChange,
    onBlur,
    onFocus,
}) => {
    const handleChange = useCallback(
        (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
            onChange(event.target.value);
        },
        [onChange],
    );

    const [focused, setFocused] = useState(false);

    const handleFocus = useCallback(() => {
        setFocused(true);
        onFocus && onFocus();
    }, [onFocus]);

    const handleBlur = useCallback(() => {
        setFocused(false);
        onBlur && onBlur();
    }, [onBlur]);

    const handleClear = useCallback(() => {
        onChange('');
    }, [onChange]);

    const typeClassName = TYPE_CLASS_NAMES[type];

    const isTextArea = nativeType === 'textarea';
    const inputId = `input_${name}`;

    return (
        <div
            className={classNames(styles.block, typeClassName, className, {
                [styles.filled]: Boolean(value),
                [styles.focused]: focused,
                [styles.error]: Boolean(error),
                [styles.textareaWrapper]: isTextArea,
            })}
        >
            {label && (
                <label htmlFor={inputId} className={styles.label}>
                    {label}
                    {required && <span className={styles.requiredSign}>*</span>}
                </label>
            )}
            <Icon
                name="cross14"
                // onMouseDown используется, так как onClick срабатывает, когда кнопка курсора
                // "отжата", а в этот момент уже успело произойти событие blur и крестик исчез
                // поэтому колбэк не срабатывал
                onMouseDown={handleClear}
                className={classNames(styles.clear, {
                    [styles.visible]:
                        Boolean(value) && focused && !hideClearButton,
                })}
            />
            {!isTextArea && (
                <InputMask
                    name={name}
                    id={inputId}
                    onFocus={handleFocus}
                    onBlur={handleBlur}
                    className={classNames(styles.input, inputClassName)}
                    value={value}
                    onChange={handleChange}
                    type={nativeType}
                    mask={mask}
                    maskPlaceholder={maskPlaceholder}
                    beforeMaskedStateChange={beforeMaskedStateChange}
                    alwaysShowMask={alwaysShowMask}
                    readOnly={readonly}
                    disabled={disabled}
                    autoComplete="off"
                />
            )}
            {isTextArea && (
                <textarea
                    name={name}
                    id={inputId}
                    onFocus={handleFocus}
                    onBlur={handleBlur}
                    className={styles.textarea}
                    value={value}
                    onChange={handleChange}
                    autoComplete="off"
                />
            )}
            <Tooltip
                content={error}
                className={styles.tooltip}
                type={TooltipType.Error}
                visible={Boolean(error && !hideErrorTooltip)}
            />
        </div>
    );
};
